import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody, ModalFooter } from '@ingka/modal';
import Button from '@ingka/button';
import Pill from '@ingka/pill';
import InputField from '@ingka/input-field';
import ImageIcon from '@ingka/ssr-icon/paths/image';
import LinkIcon from '@ingka/ssr-icon/paths/link';
import PlusIcon from '@ingka/ssr-icon/paths/plus';
import TrashIcon from '@ingka/ssr-icon/paths/trash-can';
import SlideIcon from '@ingka/ssr-icon/paths/handle-vertical';
import { DropResult } from '@hello-pangea/dnd';
import { DraggableContainer } from '@/components/containers/draggable';
import { Divider } from '@/components/layouts/divider';
import { AsyncImage } from '@/components/helpers/image';
import { useCreateImageUploadUrlMutation } from '@/redux/back-dashboard-api';
import styles from './index.module.css';

type ImageEntry = {
  id: string;
  url: string;
  // Entries attached in this modal, still pending upload
  isNew: boolean;
  file?: File;
};

const toEntries = (images: string[]): ImageEntry[] =>
  images.map((url) => ({ id: uuidv4(), url, isNew: false }));

const reorder = (list: ImageEntry[], startIndex: number, endIndex: number) => {
  const result = [...list];
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export type ImagePickerSiderProps = {
  title: string;
  images?: string[];
  mimeTypes?: string[];
  // Used to build a traceable object name for the uploaded image (location/tag).
  locationSlug?: string;
  tag: string;
  onChange: (images: string[]) => Promise<void>;
};

const DEFAULT_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
];

const ImagePickerSider: FC<ModalComponentBaseProps & ImagePickerSiderProps> = ({
  handleDismiss,
  title,
  images = [],
  mimeTypes = DEFAULT_MIME_TYPES,
  locationSlug,
  tag,
  onChange,
  ...rest
}) => {
  // Hooks
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const [entries, setEntries] = useState<ImageEntry[]>(() => toEntries(images));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingUrl, setIsAddingUrl] = useState(false);
  const [createImageUploadUrl] = useCreateImageUploadUrlMutation();
  // Uploads a single file: requests a signed URL, PUTs the file bytes to GCS,
  // then returns the resulting public URL.
  const uploadImage = useCallback(
    async (file: File): Promise<string> => {
      const {
        createImageUploadUrl: { uploadUrl, publicUrl },
      } = await createImageUploadUrl({
        input: {
          fileName: file.name,
          mimeType: file.type,
          locationSlug,
          tag,
        },
      }).unwrap();

      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      return publicUrl;
    },
    [createImageUploadUrl, locationSlug, tag]
  );
  // Event handlers
  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFilesSelected = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);

      if (files.length > 0) {
        setEntries((current) => [
          ...current,
          ...files.map((file) => ({
            id: uuidv4(),
            url: URL.createObjectURL(file),
            isNew: true,
            file,
          })),
        ]);
      }

      // Allow selecting the same file again
      event.target.value = '';
    },
    []
  );

  const handleToggleUrlInput = useCallback(() => {
    setIsAddingUrl((current) => !current);
  }, []);

  const handleAddUrl = useCallback(() => {
    const url = urlInputRef.current?.value.trim();
    if (!url) return;

    // URLs are already hosted, no upload needed for these entries
    setEntries((current) => [...current, { id: uuidv4(), url, isNew: false }]);

    if (urlInputRef.current) {
      urlInputRef.current.value = '';
    }

    setIsAddingUrl(false);
  }, []);

  const handleUrlKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleAddUrl();
      }
    },
    [handleAddUrl]
  );

  const handleRemove = useCallback((id: string) => {
    setEntries((current) => {
      const entry = current.find((item) => item.id === id);

      if (entry?.isNew) {
        URL.revokeObjectURL(entry.url);
      }

      return current.filter((item) => item.id !== id);
    });
  }, []);

  const handleDragEnd = useCallback((dropResult: DropResult) => {
    const { source, destination } = dropResult;
    if (!destination) return;

    setEntries((current) => reorder(current, source.index, destination.index));
  }, []);

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true);

    try {
      // Only the newly attached entries are uploaded, existing images are kept as-is
      const uploadedImages = await Promise.all(
        entries.map((entry) =>
          entry.isNew && entry.file
            ? uploadImage(entry.file)
            : Promise.resolve(entry.url)
        )
      );

      await onChange(uploadedImages);
      handleDismiss();
    } finally {
      setIsSubmitting(false);
    }
  }, [entries, onChange, handleDismiss]);

  return (
    <Sheets
      {...rest}
      data-testid="imagePickerSider"
      aria-label={title}
      header={<ModalHeader ariaCloseTxt={t('aria.close')} title={title} />}
      footer={
        <ModalFooter>
          <Button
            type="secondary"
            disabled={isSubmitting}
            onClick={handleDismiss}
          >
            {t('label.cancel')}
          </Button>

          <Button type="primary" loading={isSubmitting} onClick={handleConfirm}>
            {t('label.confirm')}
          </Button>
        </ModalFooter>
      }
    >
      <ModalBody>
        <input
          ref={fileInputRef}
          type="file"
          accept={mimeTypes.join(',')}
          multiple
          hidden
          data-testid="imagePickerFileInput"
          onChange={handleFilesSelected}
        />

        <div className="flex gap-x-2 mb-4">
          <Button
            fluid
            type="secondary"
            text={t('label.attachImage')}
            ssrIcon={ImageIcon}
            disabled={isAddingUrl}
            onClick={handleAttachClick}
            size="small"
          />

          <Button
            fluid
            type="secondary"
            text={t('label.fromUrl')}
            ssrIcon={LinkIcon}
            onClick={handleToggleUrlInput}
            size="small"
          />
        </div>

        {isAddingUrl && (
          <div className="flex items-center gap-x-2 mb-4">
            <InputField
              ref={urlInputRef}
              type="text"
              id="imagePickerUrl"
              label={null}
              aria-label={t('label.imageUrl')}
              className="grow"
              data-testid="imagePickerUrlInput"
              onKeyDown={handleUrlKeyDown}
            />

            <Button
              className="shrink-0"
              title={t('label.add')}
              iconOnly
              ssrIcon={PlusIcon}
              type="primary"
              size="small"
              onClick={handleAddUrl}
            />
          </div>
        )}

        <Divider />

        <DraggableContainer entries={entries} onDragEnd={handleDragEnd}>
          {(draggableProvider, entry) => (
            <div className="flex items-center py-2">
              <Pill
                type="button"
                className="h-[41px] mr-3 shrink-0"
                aria-label={t('aria.moveImage')}
                size="small"
                ssrIcon={SlideIcon}
                iconOnly
                {...draggableProvider.dragHandleProps}
              />

              <div
                className={`${styles.thumbnail} w-16 h-16 rounded overflow-hidden shrink-0 bg-skapa-neutral-2 flex items-center justify-center`}
              >
                <AsyncImage src={entry.url} alt={title} />
              </div>

              <Button
                className="ml-auto"
                title={t('label.delete')}
                iconOnly
                ssrIcon={TrashIcon}
                type="tertiary"
                size="small"
                onClick={() => handleRemove(entry.id)}
              />
            </div>
          )}
        </DraggableContainer>
      </ModalBody>
    </Sheets>
  );
};

export default ImagePickerSider;
